import { Link } from "react-router-dom";
import { Copy, Mail, MessageCircle, Facebook, Twitter, Linkedin, Download, Inbox } from "lucide-react";
import { toast } from "sonner";
import { useApp } from "../context/AppContext";

export const htmlify = (value) => {
  const text = String(value || "");
  if (!text) return "";
  if (/<[a-z][\s\S]*>/i.test(text)) return text;
  return text.split(/\n{2,}|\n/).filter(Boolean).map((line) => `<p>${line}</p>`).join("");
};

export function HtmlBlock({ html, className = "", testid }) {
  if (!html) return null;
  return <div className={`prose-ssf ${className}`} data-testid={testid} dangerouslySetInnerHTML={{ __html: htmlify(html) }} />;
}

export function PageHero({ title, sub }) {
  return (
    <section className="grain bg-navy py-20 sm:py-24">
      <div className="container-ssf">
        <h1 data-testid="page-title" className="font-display text-4xl sm:text-5xl tracking-tight text-white">{title}</h1>
        <HtmlBlock html={sub} className="mt-4 max-w-2xl text-base text-slate-300 sm:text-lg" testid="page-subtitle" />
      </div>
    </section>
  );
}

export function SectionLabel({ children }) {
  return <p className="label-eyebrow">{children}</p>;
}

export function EmptyState({ text = "Nothing published here yet." }) {
  return (
    <div data-testid="empty-state" className="card-ssf flex flex-col items-center gap-3 py-16 text-center">
      <Inbox className="h-10 w-10 text-slate-300" strokeWidth={1.5} />
      <p className="text-sm text-slate-500">{text}</p>
    </div>
  );
}

export function LazyImg({ src, alt, className }) {
  if (!src) return null;
  return <img src={src} alt={alt || ""} loading="lazy" decoding="async" className={className} />;
}

export function ShareButtons({ title, text, url }) {
  const { t } = useApp();
  const full = url || window.location.href;
  const copy = async () => {
    try { await navigator.clipboard.writeText(`${title}\n\n${text || ""}\n${full}`); toast.success("Copied to clipboard"); }
    catch (e) { toast.error("Copy failed"); }
  };
  const wa = `https://wa.me/?text=${encodeURIComponent(`${title}\n${text || ""}\n${full}`)}`;
  const fb = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(full)}`;
  const x = `https://twitter.com/intent/tweet?text=${encodeURIComponent(title)}&url=${encodeURIComponent(full)}`;
  const li = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(full)}`;
  const mail = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(`${text || ""}\n${full}`)}`;
  const cls = "inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white text-navy transition-[transform,background-color] duration-200 hover:-translate-y-0.5 hover:bg-saffron-100";
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="share-buttons">
      <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{t("share")}:</span>
      <a data-testid="share-whatsapp" className={cls} href={wa} target="_blank" rel="noreferrer" aria-label="WhatsApp"><MessageCircle size={17} /></a>
      <a data-testid="share-facebook" className={cls} href={fb} target="_blank" rel="noreferrer" aria-label="Facebook"><Facebook size={17} /></a>
      <a data-testid="share-x" className={cls} href={x} target="_blank" rel="noreferrer" aria-label="X"><Twitter size={17} /></a>
      <a data-testid="share-linkedin" className={cls} href={li} target="_blank" rel="noreferrer" aria-label="LinkedIn"><Linkedin size={17} /></a>
      <a data-testid="share-email" className={cls} href={mail} aria-label="Email"><Mail size={17} /></a>
      <button data-testid="share-copy" className={cls} onClick={copy} aria-label="Copy"><Copy size={17} /></button>
    </div>
  );
}

export function WorkIcon({ name, className = "h-6 w-6" }) {
  const { BookOpen, HeartPulse, ShieldCheck, Users, Home, HandHeart, Wrench, Megaphone, Baby, LifeBuoy, Sparkles } = require("lucide-react");
  const map = { BookOpen, HeartPulse, ShieldCheck, Users, Home, HandHeart, Wrench, Megaphone, Baby, LifeBuoy };
  const Icon = map[name] || Sparkles;
  return <Icon className={className} strokeWidth={1.5} />;
}

export function LinkBtn({ to, children, kind = "primary", testid }) {
  const cls = kind === "primary" ? "btn-primary" : kind === "dark" ? "btn-dark" : "btn-ghost";
  return <Link data-testid={testid} to={to} className={cls}>{children}</Link>;
}
